/**
 * @file thunks.js
 * @fileoverview API functions which are called by the jobs redux thunks.
 *
 * @typedef {import('BMapsSrc/jobs/JobDefinition').JobStatusOptions} JobStatusOptions
 * @typedef {import('BMapsSrc/jobs/JobDefinition').JobTypeOptions} JobTypeOptions
 * @typedef {import('BMapsSrc/jobs/JobsApi').postJobResponse} postJobResponse
 * @typedef {import('BMapsSrc/jobs/JobsApi').getJobsResponse} getJobsResponse
 * @typedef {import('BMapsSrc/jobs/JobsApi').deleteJobResponse} deleteJobResponse
 * @typedef {import('BMapsSrc/jobs/JobsApi').loadJobResponse} loadJobResponse
 *
 */
import {
    deleteJob, getJobs, postJob, loadJobResult,
} from 'BMapsSrc/jobs/JobsApi';
import { jobDefinitions } from 'BMapsSrc/jobs/JobDefinition';
import * as jobActions from '.';

/**
 * @returns {{ results?: getJobsResponse, error?: string }}
 */
export const fetchJobs = () => async (dispatch) => {
    const jobs = await getJobs();
    if (!jobs.error && jobs.results) {
        dispatch(jobActions.loadJobs(jobs.results));
        return { results: jobs.results };
    }
    return { error: jobs.error };
};

/**
 *
 * @param {JobTypeOptions} jobType
 * @param {Object} data
 * @param {Object} stash
 *
 * @returns {{ results?: postJobResponse, error?: string }}
 */
export const createJob = (jobType, data, stash) => async (dispatch) => {
    const jobDefinition = jobDefinitions[jobType];
    if (!jobDefinition) return { error: `Unrecognized job type: ${jobType}` };
    const requestData = jobDefinition.prepareRequest(data, stash);
    const jobResult = await postJob(requestData);
    if (!jobResult.error && jobResult.results) {
        dispatch(jobActions.addJob(jobResult.results));
        return { results: jobResult.results };
    }
    return { error: jobResult.error, results: jobResult.results };
};

/**
 * @param {string} jobid
 * @returns {{ results?: deleteJobResponse, error?: string }}
 */
export const removeJob = (jobid) => async (dispatch, getState) => {
    const job = getState().jobs.jobs.find((j) => j.jobid === jobid);
    if (!job) return { error: `The requested job could not be found: ${jobid}` };
    const { results, error } = await deleteJob(jobid);
    if (error) return dispatch(setJobError(jobid, error));
    if (!results || !results.success) return dispatch(setJobError(jobid, 'Failed to delete job.'));
    dispatch(jobActions.removeJob(jobid));

    return { results };
};

/**
 *
 * @param {string} jobid
 * @param {object} options
 * @returns {{ results?: loadJobResponse, error?: string }}
 */
export const loadResult = (jobid, options=null) => async (dispatch, getState) => {
    let { results, error } = await loadJobResult(jobid);
    const job = getState().jobs.jobs.find((j) => j.jobid === jobid);
    if (!job) return { error: `The requested job could not be found: ${jobid}` };
    // Update the local job based on response.
    dispatch(jobActions.setJob({
        ...job,
        delivered: new Date().toISOString(),
        state: results?.state || job.state, // allow job state override from loadJobResult packet
    }));
    if (error) return dispatch(setJobError(jobid, error));
    const jobDefinition = jobDefinitions[job.jobtype];
    if (!jobDefinition) return dispatch(setJobError(jobid, `Unrecognized job type: ${job.jobtype}`));
    ({ results, error } = await jobDefinition.integrateResults(job, results, options));
    if (error) return dispatch(setJobError(jobid, error));

    return { results };
};

/**
 * @param {string} jobid
 * @returns {{ results?: loadJobResponse, error?: string }}
 */
export const updateJobErrors = (jobid) => async (dispatch, getState) => {
    const { results, error } = await loadJobResult(jobid);
    const job = getState().jobs.jobs.find((j) => j.jobid === jobid);
    if (!job) return { error: `The requested job could not be found: ${jobid}` };
    dispatch(jobActions.setJob({ ...job, delivered: new Date().toISOString() }));
    // currently only errors are added to localJobState
    // once more is needed this can be updated
    if (error) dispatch(setJobError(jobid, error));
    return { results };
};

/**
 * @param {string} jobid
 * @param {string} error
 * @returns {{ error: string }}
 */
const setJobError = (jobid, error) => (dispatch) => {
    dispatch(jobActions.updateLocalJobState({ jobid, error }));
    return { error };
};
